import { Box, Card, CardContent, Chip, Skeleton } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { ChartConfig } from "../components/ui/chart";
import { useBlockedAccessGuard } from "../hooks/useBlockedAccessGuard";
import { financeApi } from "../lib/api";
import { appToday, useDevDateOverride } from "../lib/devDate";
import { getCategoryIconComponent } from "../lib/categoryIcons";
import { queryKeys } from "../lib/queryKeys";
import type { Group } from "../lib/types";
import { MonthlyExpenseBreakdown, aggregateParentCategories, collapsedOtherCategoryItems, expenseCategoryKey } from "../features/statistics/MonthlyExpenseBreakdown";
import type { MonthlyOperationType } from "../features/statistics/MonthlyOperationsPreview";
import { MonthlyOperationsWithEditor } from "../features/statistics/MonthlyOperationsWithEditor";
import { StatisticsAccounts } from "../features/statistics/StatisticsAccounts";
import { StatisticsAggregationWarning } from "../features/statistics/StatisticsAggregationWarning";
import { StatisticsFilterBar } from "../features/statistics/StatisticsFilterBar";
import { StatisticsKpis } from "../features/statistics/StatisticsKpis";
import { StatisticsPayees } from "../features/statistics/StatisticsPayees";
import { CategoryChangesCard, CategoryRanking, ExpenseStructureCard, categoryPalette } from "../features/statistics/StatisticsRankings";
import { StatisticsTabs } from "../features/statistics/StatisticsTabs";
import { StatisticsTrendCharts } from "../features/statistics/StatisticsTrendCharts";
import { bucketLabel, formatPeriod } from "../features/statistics/formatters";
import { buildMonthlyBreakdownModel, statisticsAccountOwnerLabel } from "../features/statistics/monthlyBreakdownModel";
import { groupReportExpenses } from "../features/statistics/statisticsModels";
import { useMonthlyOperationsQuery } from "../features/statistics/useMonthlyOperationsQuery";
import { useStatisticsUrlState } from "../features/statistics/useStatisticsUrlState";

type StatisticsPageProps = {
  group: Group;
  onBlockedAccess?: () => void;
};

export function StatisticsPage({ group, onBlockedAccess }: StatisticsPageProps) {
  const navigate = useNavigate();
  const asOfDate = useDevDateOverride();

  const categoriesQuery = useQuery({ queryKey: queryKeys.categories(group.id), queryFn: () => financeApi.categories(group.id) });
  const accountsQuery = useQuery({ queryKey: queryKeys.accounts(group.id), queryFn: () => financeApi.accounts(group.id) });
  const membersQuery = useQuery({ queryKey: queryKeys.members(group.id), queryFn: () => financeApi.members(group.id) });
  const tagsQuery = useQuery({ queryKey: queryKeys.tags(group.id), queryFn: () => financeApi.tags(group.id) });
  const currenciesQuery = useQuery({ queryKey: queryKeys.currencies(group.id), queryFn: () => financeApi.currencies(group.id) });
  const categories = categoriesQuery.data || [];
  const accounts = accountsQuery.data || [];
  const members = membersQuery.data || [];
  const tags = tagsQuery.data || [];
  const currencies = currenciesQuery.data || [];
  const {
    view,
    month,
    filters,
    setFilters,
    merchantInput,
    setMerchantInput,
    selectedParentCategory,
    setSelectedParentCategory,
    selectedChildCategories,
    setSelectedChildCategories,
    otherCategoryActive,
    setOtherCategoryActive,
    changeMonth,
    changeView,
    setPreset,
    activePreset,
    clearFilters,
    changeOverviewType,
    deleteOverviewFilter,
  } = useStatisticsUrlState({ asOfDate, categories });
  const overviewType = filters.type === "correction" ? "" : filters.type;
  const reportFilters = useMemo(() => view === "overview" ? { ...filters, type: "" as const, categoryId: "", merchant: "" } : filters, [filters, view]);
  const reportQuery = useQuery({
    queryKey: [...queryKeys.statistics(group.id, reportFilters), asOfDate],
    queryFn: () => financeApi.statistics(group.id, { ...reportFilters, limit: 50 }),
    placeholderData: (previous) => previous,
  });
  const incomeCategoryFilters = useMemo(() => ({ ...reportFilters, type: "income" as const }), [reportFilters]);
  const incomeCategoryQuery = useQuery({
    queryKey: [...queryKeys.statistics(group.id, incomeCategoryFilters), asOfDate],
    queryFn: () => financeApi.statistics(group.id, { ...incomeCategoryFilters, limit: 50 }),
    enabled: view === "overview" && overviewType === "income",
  });
  const reportCategoryItems = overviewType === "income" ? incomeCategoryQuery.data?.categories_by_amount || [] : reportQuery.data?.categories_by_amount || [];
  const reportCategories = categoriesQuery.data || [];
  const selectedLeafCategoryId = overviewType === "income" || overviewType === "expense"
    ? selectedChildCategories.length === 1 && !reportCategories.some((category) => category.parent_id === selectedChildCategories[0])
      ? selectedChildCategories[0]
      : selectedParentCategory && selectedParentCategory !== "uncategorized" && !selectedChildCategories.length && !reportCategories.some((category) => category.parent_id === selectedParentCategory)
        ? selectedParentCategory
        : ""
    : "";
  const categoryLeafFilters = useMemo(() => ({ ...reportFilters, type: overviewType || undefined, categoryId: selectedLeafCategoryId }), [overviewType, reportFilters, selectedLeafCategoryId]);
  const categoryLeafQuery = useQuery({
    queryKey: [...queryKeys.statistics(group.id, categoryLeafFilters), asOfDate],
    queryFn: () => financeApi.statistics(group.id, { ...categoryLeafFilters, limit: 50 }),
    enabled: view === "overview" && (overviewType === "income" || overviewType === "expense") && Boolean(selectedLeafCategoryId),
  });
  const accountOptionsFilters = useMemo(() => ({
    ...reportFilters,
    accountId: "",
    type: selectedLeafCategoryId ? overviewType || undefined : reportFilters.type,
    categoryId: selectedLeafCategoryId,
  }), [overviewType, reportFilters, selectedLeafCategoryId]);
  const accountOptionsQuery = useQuery({
    queryKey: [...queryKeys.statistics(group.id, accountOptionsFilters), asOfDate],
    queryFn: () => financeApi.statistics(group.id, { ...accountOptionsFilters, limit: 50 }),
    enabled: view === "overview" && (overviewType === "transfer" || Boolean(selectedLeafCategoryId)),
  });
  const reportParentItems = aggregateParentCategories(reportCategoryItems, reportCategories);
  const otherCategorySource = selectedParentCategory && selectedParentCategory !== "uncategorized"
    ? reportCategoryItems.filter((item) => item.category_id && reportCategories.find((category) => category.id === item.category_id)?.parent_id === selectedParentCategory)
    : reportParentItems;
  const otherCategoryItems = collapsedOtherCategoryItems(otherCategorySource);
  const otherCategoryIds = otherCategoryItems.map(expenseCategoryKey);
  const { operationsQuery } = useMonthlyOperationsQuery({
    group,
    filters,
    view,
    selectedParentCategory,
    selectedChildCategories,
    otherCategoryActive,
    otherCategoryIds,
  });

  useBlockedAccessGuard([categoriesQuery.error, accountsQuery.error, membersQuery.error, tagsQuery.error, currenciesQuery.error, reportQuery.error, incomeCategoryQuery.error, categoryLeafQuery.error, accountOptionsQuery.error, operationsQuery.error], onBlockedAccess);

  const report = reportQuery.data;
  const currency = report?.base_currency || group.base_currency || "EUR";
  const isEmptyPeriod = Boolean(report && report.totals.transaction_count === 0);
  const accountReport = selectedLeafCategoryId ? categoryLeafQuery.data : report;
  const monthlyBreakdown = useMemo(() => buildMonthlyBreakdownModel({
    report,
    accountReport,
    accountOptionsReport: accountOptionsQuery.data,
    reportCategoryItems,
    accounts,
    members,
    overviewType,
    selectedLeafCategoryId,
    selectedAccountId: filters.accountId,
    fallbackCurrency: currency,
  }), [
    accountOptionsQuery.data,
    accountReport,
    accounts,
    currency,
    filters.accountId,
    members,
    overviewType,
    report,
    reportCategoryItems,
    selectedLeafCategoryId,
  ]);
  const accountOwnerLabel = (accountId: string) => statisticsAccountOwnerLabel(accountId, accounts, members);

  const cashFlowData = useMemo(() => (report?.cash_flow || []).map((point) => ({
    ...point,
    label: bucketLabel(point.bucket, filters.bucket),
    income: Number(point.income),
    expense: Number(point.expense),
    net: Number(point.net),
    cumulative_expense: Number(point.cumulative_expense),
    previous_cumulative_expense: Number(point.previous_cumulative_expense),
  })), [report, filters.bucket]);

  const stacked = useMemo(() => {
    const names: string[] = [];
    const colors: Record<string, string> = {};
    for (const point of report?.cash_flow || []) {
      for (const entry of point.categories || []) {
        if (!names.includes(entry.category_name)) {
          names.push(entry.category_name);
          colors[entry.category_name] = entry.color || (entry.category_name === "Other" ? "#94a3b8" : categoryPalette[names.length % categoryPalette.length]);
        }
      }
    }
    return {
      names,
      colors,
      data: (report?.cash_flow || []).map((point) => ({ label: bucketLabel(point.bucket, filters.bucket), ...Object.fromEntries((point.categories || []).map((entry) => [entry.category_name, Number(entry.amount)])) })),
      config: Object.fromEntries(names.map((name) => [name, { label: name, color: colors[name] }])) as ChartConfig,
    };
  }, [report, filters.bucket]);

  const groupedExpenses = useMemo(() => groupReportExpenses(report, currency), [report, currency]);

  const monthlySavings = useMemo(() => (report?.monthly_savings || []).filter((point) => point.rate !== null && point.rate !== undefined).map((point) => ({
    label: bucketLabel(point.month, "month"), rate: Math.round(point.rate as number), income: Number(point.income), expense: Number(point.expense), net: Number(point.net),
  })), [report]);

  const selectedParentCategoryItem = categories.find((category) => category.id === selectedParentCategory);
  const selectedChildCategoryItems = selectedChildCategories.map((categoryId) => {
    if (categoryId === "uncategorized") return { id: categoryId, name: "Uncategorized", color: "#64748b", icon: "tag" };
    const category = categories.find((item) => item.id === categoryId);
    return category ? { id: category.id, name: category.name, color: category.color, icon: category.icon } : null;
  }).filter((category): category is { id: string; name: string; color: string; icon: string } => Boolean(category));
  const categoryFilterIcon = (icon?: string | null) => {
    const Icon = getCategoryIconComponent(icon);
    return <Icon aria-hidden="true" />;
  };
  const selectedCategoryName = selectedChildCategoryItems.length
    ? selectedChildCategoryItems.map((category) => category.name).join(" or ")
    : otherCategoryActive
      ? "Other"
    : selectedParentCategory === "uncategorized"
      ? "Uncategorized"
      : selectedParentCategoryItem?.name;
  const categoryFilterChips = selectedParentCategory === "uncategorized"
    ? [{ key: "category", label: "Uncategorized", color: "#64748b", icon: categoryFilterIcon("tag") }]
    : selectedParentCategory
      ? [
          { key: "category-parent", label: selectedParentCategoryItem?.name || "Category", color: selectedParentCategoryItem?.color, icon: categoryFilterIcon(selectedParentCategoryItem?.icon) },
          ...(otherCategoryActive ? [{ key: "category-other", label: "Other", color: "#94a3b8", icon: categoryFilterIcon("dots") }] : []),
          ...selectedChildCategoryItems.map((category) => ({ key: `category-child:${category.id}`, label: category.name, color: category.color, icon: categoryFilterIcon(category.icon) })),
        ]
      : otherCategoryActive
        ? [
            { key: "category-other", label: "Other", color: "#94a3b8", icon: categoryFilterIcon("dots") },
            ...selectedChildCategoryItems.map((category) => ({ key: `category-child:${category.id}`, label: category.name, color: category.color, icon: categoryFilterIcon(category.icon) })),
          ]
        : [];
  const overviewFilterChips = [
    overviewType ? { key: "type", label: overviewType === "expense" ? "Expenses" : overviewType === "income" ? "Income" : "Transfers" } : null,
    ...categoryFilterChips,
    filters.compare === "year" ? { key: "compare", label: "Same period last year" } : null,
    filters.userId ? { key: "user", label: members.find((member) => member.user_id === filters.userId)?.display_name || "Person" } : null,
    filters.accountId ? { key: "account", label: `${accounts.find((account) => account.id === filters.accountId)?.name || "Account"} · ${accountOwnerLabel(filters.accountId)}` } : null,
    ...filters.tagIds.map((tagId) => ({ key: `tag:${tagId}`, label: tags.find((tag) => tag.id === tagId)?.name || "Tag" })),
  ].filter((chip): chip is { key: string; label: string; color?: string } => Boolean(chip));
  const previousLabel = report ? formatPeriod(report.previous_period.date_from, report.previous_period.date_to) : "";
  const drillDates = () => `from=${filters.dateFrom}&to=${filters.dateTo}`;
  const drillCategory = (categoryId: string | null | undefined) => navigate(categoryId ? `/records?category=${encodeURIComponent(categoryId)}&${drillDates()}` : `/records?${drillDates()}`);
  const drillMerchant = (merchant: string) => navigate(`/records?q=${encodeURIComponent(merchant)}&${drillDates()}`);
  const drillAccount = (accountId: string) => navigate(`/records?account=${encodeURIComponent(accountId)}&${drillDates()}`);
  const monthlyRecordsUrl = () => {
    const params = new URLSearchParams({ from: filters.dateFrom, to: filters.dateTo });
    if (selectedParentCategory === "uncategorized") params.set("uncategorized", "1");
    else if (selectedChildCategories.length === 1) params.set("category", selectedChildCategories[0]);
    else if (otherCategoryActive && otherCategoryIds.length === 1) params.set("category", otherCategoryIds[0]);
    else if (selectedParentCategory) params.set("category", selectedParentCategory);
    if (filters.type && filters.type !== "correction") params.set("type", filters.type);
    if (filters.userId) params.set("user", filters.userId);
    if (filters.accountId) params.set("account", filters.accountId);
    filters.tagIds.forEach((tagId) => params.append("tag", tagId));
    if (filters.merchant) params.set("q", filters.merchant);
    return `/records?${params.toString()}`;
  };

  return (
    <>
      <header className="topbar"><div><h1>Statistics</h1><p>Understand monthly spending and explore the details behind it.</p></div><Chip className="chip" label={reportQuery.isFetching ? "Updating" : currency} size="small" /></header>
      <section className={`content statistics-content${reportQuery.isFetching && report ? " refreshing" : ""}`}>
        <StatisticsTabs value={view} onChange={changeView} />
        {report ? <StatisticsAggregationWarning report={report} /> : null}
        {view === "overview" ? <Card className="records-panel monthly-overview-shell"><CardContent className="monthly-overview-content">
          {reportQuery.isError ? <p className="records-error" role="alert">Could not load statistics. Please try again.</p> : null}
          {reportQuery.isLoading ? <><Skeleton variant="rounded" height={96} /><Skeleton variant="rounded" height={420} /></> : null}
          {report ? <>
            <div className="monthly-overview-summary-row">
              <StatisticsKpis report={report} currency={currency} previousLabel={previousLabel} />
              <StatisticsFilterBar compact monthlyMode hasExternalFilters={Boolean(selectedParentCategory || selectedChildCategories.length || otherCategoryActive)} filters={filters} merchantInput={merchantInput} categories={categories} members={members} accounts={accounts} tags={tags} activePreset={activePreset} onChange={setFilters} onMerchantInput={setMerchantInput} onPreset={setPreset} onReset={clearFilters} />
            </div>
            <div className="monthly-overview-explorer">
              <MonthlyExpenseBreakdown
                items={monthlyBreakdown.chartItems}
                categories={categories}
                total={monthlyBreakdown.chartTotal}
                currency={monthlyBreakdown.chartCurrency}
                selectedParentCategory={selectedLeafCategoryId ? "" : selectedParentCategory}
                selectedChildCategories={selectedLeafCategoryId ? [] : selectedChildCategories}
                otherActive={selectedLeafCategoryId ? false : otherCategoryActive}
                onSelectParentCategory={(category) => {
                  setSelectedParentCategory((current) => current === category ? "" : category);
                  setSelectedChildCategories([]);
                  setOtherCategoryActive(false);
                  setFilters((current) => ({ ...current, accountId: "" }));
                }}
                onToggleChildCategory={(category) => {
                  setSelectedChildCategories((current) => current.includes(category) ? current.filter((item) => item !== category) : [...current, category]);
                  setFilters((current) => ({ ...current, accountId: "" }));
                }}
                onSelectOther={() => {
                  setOtherCategoryActive(true);
                  setSelectedChildCategories([]);
                  setFilters((current) => ({ ...current, accountId: "" }));
                }}
                month={month}
                onMonthChange={changeMonth}
                today={appToday()}
                filterChips={overviewFilterChips}
                onDeleteFilter={deleteOverviewFilter}
                totalLabel={monthlyBreakdown.chartLabel}
                categoryInteraction={(overviewType === "expense" || overviewType === "income") && !selectedLeafCategoryId}
                accountFilters={monthlyBreakdown.showAccountBreakdown ? monthlyBreakdown.accountFilters : undefined}
                onToggleAccount={(accountId) => setFilters((current) => ({ ...current, accountId: current.accountId === accountId ? "" : accountId }))}
                onSelectBreakdownItem={!overviewType
                  ? (type) => changeOverviewType(type as MonthlyOperationType)
                  : overviewType === "transfer" || Boolean(selectedLeafCategoryId)
                    ? (accountId) => setFilters((current) => ({ ...current, accountId: current.accountId === accountId ? "" : accountId }))
                    : undefined}
              />
              <MonthlyOperationsWithEditor
                group={group}
                transactions={operationsQuery.data?.items || []}
                accounts={accounts}
                categories={categories}
                members={members}
                tags={tags}
                currencies={currencies}
                selectedCategoryName={selectedCategoryName}
                type={(filters.type === "correction" ? "" : filters.type) as MonthlyOperationType}
                search={merchantInput}
                loading={operationsQuery.isLoading || operationsQuery.isFetching}
                error={operationsQuery.isError}
                hasMore={Boolean(operationsQuery.data?.has_more)}
                onTypeChange={changeOverviewType}
                onSearchChange={setMerchantInput}
                onOpenAll={() => navigate(monthlyRecordsUrl())}
                onRetry={() => void operationsQuery.refetch()}
              />
            </div>
          </> : null}
        </CardContent></Card> : <>
          <StatisticsFilterBar filters={filters} merchantInput={merchantInput} categories={categories} members={members} accounts={accounts} tags={tags} activePreset={activePreset} onChange={setFilters} onMerchantInput={setMerchantInput} onPreset={setPreset} onReset={clearFilters} />
          {reportQuery.isError ? <p className="records-error" role="alert">Could not load statistics. Please try again.</p> : null}
          {reportQuery.isLoading ? <><Skeleton variant="rounded" height={340} /><Skeleton variant="rounded" height={280} /></> : null}
          {report && isEmptyPeriod ? <p className="status stat-auto-note">No records in this period.</p> : null}
          {report && !isEmptyPeriod ? <>
            {view === "trends" ? <StatisticsTrendCharts cashFlowData={cashFlowData} stacked={stacked} monthlySavings={monthlySavings} currency={currency} periodLabel={formatPeriod(report.period.date_from, report.period.date_to)} previousLabel={previousLabel} monthBuckets={filters.bucket === "month"} /> : null}
            {view === "categories" ? <Box className="stat-grid"><ExpenseStructureCard items={report.categories_by_amount} currency={currency} onDrill={drillCategory} /><CategoryChangesCard items={report.categories_by_delta || []} currency={currency} previousLabel={previousLabel} onDrill={drillCategory} /><CategoryRanking title="Popular categories" subtitle="By frequency" items={report.categories_by_count} currency={currency} onDrill={drillCategory} /></Box> : null}
            {view === "payees" ? <StatisticsPayees report={report} currency={currency} expenses={groupedExpenses} onMerchant={drillMerchant} onCategory={drillCategory} /> : null}
            {view === "accounts" ? <StatisticsAccounts report={report} onAccount={drillAccount} /> : null}
          </> : null}
        </>}
      </section>
    </>
  );
}
